import { zodResolver } from "@hookform/resolvers/zod";
import { SessionContextValue } from "next-auth/react";
import Image from "next/image";
import React, { useEffect, useRef, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { constants } from "~/common/constants";
import { useAnalytics } from "~/components/Analytics";
import { NewsletterPrivacyPolicy } from "~/components/NewsletterPrivacyPolicy";
import { useToastManager } from "~/components/ToastManager";
import { TurnstileWidget } from "~/components/TurnstileWidget";
import { upsertEmailLists } from "~/lib/client/endilApiCalls";
import { useTurnstileHelper } from "~/lib/client/useTurnstileHelper";
import { getValueOrDefault } from "~/lib/shared/getValueOrDefault";
import { EMAIL_FIELD_SCHEMA } from "~/lib/shared/schemas/fieldSchemas";
import { Body, Button, Icon, Input, Link } from "~/ui-library";
import { CheckIcon } from "~/ui-library/icons";
import { linkStyles } from "~/ui-library/shared-styles/shared-link-styles";

export type StickyNewsletterProps = {
  adType: "sticky_newsletter";
  newsletterDescription: string;
  imageSrc: string | null;
  /** iterable id used to update the user's newsletter preferences */
  iterableListId: number;
  /** slug used for event tracking */
  newsletterSlug: string;
  newsletterTitle: string;
  confirmationTitle: string;
  confirmationDescription: string;
  confirmationCtaHref: string;
  confirmationCtaText: string;
  /** client side values, should not be set on the server side */
  defaultEmail?: string | null;
  /** client side values, should not be set on the server side */
  status?: SessionContextValue["status"];
};

const createSchema = (requireTurnstile: boolean) =>
  z.object({
    email: EMAIL_FIELD_SCHEMA,
    [constants.TURNSTILE_RESULT_PROPERTY]: requireTurnstile
      ? z.string()
      : z.string().nullable().optional(),
  });

type NewsletterFormData = z.infer<ReturnType<typeof createSchema>>;

type SignupFormProps = Pick<
  StickyNewsletterProps,
  "iterableListId" | "newsletterSlug" | "defaultEmail" | "status"
> & { onSignup: () => void };

const SignupForm: React.FC<SignupFormProps> = ({
  iterableListId,
  newsletterSlug,
  onSignup,
  defaultEmail,
  status,
}) => {
  const init = useRef(false);

  const {
    enableTurnstile,
    resetTurnstile,
    setTurnstileResult,
    trackTurnstileError,
  } = useTurnstileHelper();

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
    reset,
    setValue,
  } = useForm<NewsletterFormData>({
    defaultValues: {
      email: "",
    },
    mode: "onBlur",
    resolver: zodResolver(createSchema(enableTurnstile)),
  });

  const { analyticsEnhancedTrack } = useAnalytics();
  const { addToast } = useToastManager();

  useEffect(() => {
    if (defaultEmail && !init.current && status !== "loading") {
      init.current = true;
      setValue("email", getValueOrDefault(defaultEmail, ""), {
        shouldValidate: true,
      });
    }
  }, [defaultEmail, setValue, status]);

  const onSubmit: SubmitHandler<NewsletterFormData> = async ({
    email,
    turnstileResult,
  }) => {
    try {
      analyticsEnhancedTrack("Sticky Newsletter Sign-Up", {
        newsletter: newsletterSlug,
      });

      await upsertEmailLists({
        email,
        listIds: [iterableListId],
        turnstileResult,
      });
      reset({
        email: "",
      });
      setValue("email", email, { shouldDirty: true });

      resetTurnstile();
      setValue("turnstileResult", undefined);
      onSignup();
    } catch (err) {
      resetTurnstile();
      setValue("turnstileResult", undefined);
      addToast("danger", "Something went wrong, please try again!");
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-col gap-2">
        <div>
          <Input
            disabled={isSubmitting || status === "loading"}
            placeholder="Enter your email"
            label="Email address"
            hideLabel
            fullWidth={true}
            {...register("email", { required: true })}
            error={errors.email?.message}
          />
        </div>
        <div className="flex items-center gap-x-4">
          {enableTurnstile ? (
            <TurnstileWidget
              onVerify={(token) => {
                setTurnstileResult(token);
                setValue("turnstileResult", token, { shouldValidate: true });
              }}
              onError={trackTurnstileError}
            />
          ) : null}
          <Button
            fullWidth
            type="submit"
            variant="primary"
            size="none"
            className="h-fit min-w-[104px] whitespace-nowrap py-3 lg:min-w-[140px]"
            updating={isSubmitting}
          >
            Sign Up
          </Button>
        </div>
      </div>
    </form>
  );
};

export const StickyNewsletter: React.FC<StickyNewsletterProps> = ({
  newsletterDescription,
  imageSrc,
  newsletterTitle,
  confirmationDescription,
  confirmationTitle,
  confirmationCtaHref,
  confirmationCtaText,
  ...formProps
}) => {
  const [hasSignedUp, setHasSignedUp] = useState(false);

  const signupPrompt = (
    <div className="flex items-center gap-l">
      {imageSrc ? (
        <div className="relative hidden h-[140px] w-full max-w-[248px] md:block">
          <Image alt="" src={imageSrc} fill />
        </div>
      ) : null}
      <div className="flex-1 md:max-w-[550px]">
        <Body size="large" bold className="mb-xxs">
          {newsletterTitle}
        </Body>
        <Body className="mb-s">{newsletterDescription}</Body>
        <SignupForm {...formProps} onSignup={() => setHasSignedUp(true)} />
        <Body size="extraSmall" className="mt-xs">
          <NewsletterPrivacyPolicy
            linkClassName={linkStyles()}
            preamble="By clicking “sign up”,"
          />
        </Body>
      </div>
    </div>
  );

  const confirmationMessage = (
    <div
      className="flex flex-col items-center justify-center gap-xs text-center"
      data-testid="sticky-newsletter-confirmation"
    >
      <div className="inline-flex size-l items-center justify-center rounded-full bg-brand-green-100 text-brand-green">
        <Icon SvgIcon={CheckIcon} size="small" />
      </div>
      <Body size="large" bold>
        {confirmationTitle}
      </Body>
      <Body>{confirmationDescription}</Body>
      <Link href={confirmationCtaHref} className={linkStyles()}>
        <Body size="small">{confirmationCtaText}</Body>
      </Link>
    </div>
  );

  return hasSignedUp ? confirmationMessage : signupPrompt;
};
